import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import "./VocabTooltip.css";

const PLACEMENT_CLASS = {
  above: "",
  below: "vocab-tooltip--below",
  left: "vocab-tooltip--left",
  right: "vocab-tooltip--right",
};

function getPortalStyle(placement, rect) {
  const gap = 8;
  const scale = getAppScale();
  const scaled = scale === 1 ? "" : ` scale(${scale})`;

  switch (placement) {
    case "below":
      return {
        top: rect.bottom + gap,
        left: rect.left + rect.width / 2,
        transform: `translateX(-50%)${scaled}`,
        transformOrigin: "top center",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - gap,
        transform: `translate(-100%, -50%)${scaled}`,
        transformOrigin: "right center",
      };
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + gap,
        transform: `translateY(-50%)${scaled}`,
        transformOrigin: "left center",
      };
    case "above":
    default:
      return {
        top: rect.top - gap,
        left: rect.left + rect.width / 2,
        transform: `translate(-50%, -100%)${scaled}`,
        transformOrigin: "bottom center",
      };
  }
}

export default function MenuIconTooltip({
  children,
  tooltip,
  placement = "above",
  className = "",
  portal = false,
  arrow = true,
  zIndex,
  style,
}) {
  const wrapRef = useRef(null);
  const dismissedRef = useRef(false);
  const [dismissed, setDismissed] = useState(false);
  const [portalVisible, setPortalVisible] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);
  const placementClass = PLACEMENT_CLASS[placement] || "";

  const updatePortalPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    setPortalStyle(getPortalStyle(placement, el.getBoundingClientRect()));
  }, [placement]);

  const showPortalTooltip = useCallback(() => {
    if (dismissedRef.current) return;
    updatePortalPosition();
    setPortalVisible(true);
  }, [updatePortalPosition]);

  const hidePortalTooltip = useCallback(() => {
    setPortalVisible(false);
  }, []);

  const dismissTooltip = useCallback(() => {
    dismissedRef.current = true;
    setDismissed(true);
    setPortalVisible(false);
  }, []);

  const releaseTooltip = useCallback(() => {
    dismissedRef.current = false;
    setDismissed(false);
  }, []);

  useEffect(() => {
    if (!portal || !portalVisible) return undefined;
    const hide = () => setPortalVisible(false);
    document.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
    };
  }, [portal, portalVisible]);

  const tooltipClassName = [
    "vocab-tooltip",
    "vocab-tooltip--wide",
    placementClass,
    portal ? "vocab-tooltip--portal" : "",
    arrow ? "" : "vocab-tooltip--no-arrow",
  ]
    .filter(Boolean)
    .join(" ");

  const portaledTooltip =
    portal &&
    portalVisible &&
    portalStyle &&
    createPortal(
      <span
        className={tooltipClassName}
        style={zIndex ? { ...portalStyle, zIndex } : portalStyle}
        role="tooltip"
      >
        {tooltip}
      </span>,
      getAppPortalRoot()
    );

  return (
    <div
      ref={wrapRef}
      className={`vocab-tooltip-wrap menu-icon-tooltip${dismissed ? " vocab-tooltip-wrap--off" : ""} ${className}`.trim()}
      style={style}
      onMouseEnter={portal ? showPortalTooltip : undefined}
      onMouseLeave={() => {
        releaseTooltip();
        if (portal) hidePortalTooltip();
      }}
      onPointerDown={dismissTooltip}
      onFocus={(event) => {
        if (!portal || dismissedRef.current) return;
        const target = event.target;
        if (target instanceof Element && target.matches(":focus-visible")) showPortalTooltip();
      }}
      onBlur={portal ? hidePortalTooltip : undefined}
    >
      {children}
      {!portal && (
        <span className={tooltipClassName} role="tooltip">
          {tooltip}
        </span>
      )}
      {portaledTooltip}
    </div>
  );
}
